import { DestroyRef, Injectable, signal, computed, inject, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, switchMap, catchError, EMPTY, map } from 'rxjs';
import type { TransactionDetailResponse } from '../models';
import { TransactionService, NotificationService } from '../services';
import { extractErrorMessage } from '@shared/utils/http-error.util';

/**
 * TransactionDetailStore
 *
 * Owns the transaction-detail concern extracted from `TransactionStore`: an
 * in-memory cache of detail responses, the currently-viewed transaction id, and
 * the cancellable request pipeline that fetches details on demand.
 *
 * The cache is **stale-while-revalidate**, not read-through: a cached response is
 * served instantly so re-entering a detail page paints without a spinner, but the
 * request always fires so a status that changed server-side is picked up without a
 * browser refresh.
 *
 * `root`-provided to preserve the original singleton cache semantics. Public
 * signals/methods are re-exposed unchanged by `TransactionStore`.
 */
@Injectable({ providedIn: 'root' })
export class TransactionDetailStore {
  private readonly transactionService = inject(TransactionService);
  private readonly notificationService = inject(NotificationService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly detailRequest$ = new Subject<string>();

  private readonly _transactionDetailCache = signal<Map<string, TransactionDetailResponse>>(
    new Map(),
  );
  private readonly _currentTransactionId = signal<string | null>(null);
  private readonly _detailLoading = signal<boolean>(false);
  private readonly _detailError = signal<string | null>(null);

  readonly detailLoading = this._detailLoading.asReadonly();
  readonly detailError = this._detailError.asReadonly();

  /** Current transaction detail (cached), or `null` when none is loaded. */
  readonly currentTransactionDetail = computed<TransactionDetailResponse | null>(() => {
    const id = this._currentTransactionId();
    if (!id) return null;
    return this._transactionDetailCache().get(id) || null;
  });

  constructor() {
    // Detail loading pipeline. switchMap cancels the previous in-flight request
    // when the user navigates between detail pages quickly, preventing a stale
    // response from overwriting the correct one. The transactionId is carried
    // through so the subscribe handler knows which cache key to write.
    this.detailRequest$
      .pipe(
        switchMap((transactionId) => {
          // A cached entry is already on screen, so this request is a silent
          // background revalidation: no spinner over visible content, and a
          // failure must leave the last known good data untouched.
          const hasCached = this._transactionDetailCache().has(transactionId);
          this._detailLoading.set(!hasCached);
          if (!hasCached) this._detailError.set(null);

          return this.transactionService.getTransactionDetail(transactionId).pipe(
            map((response) => ({ transactionId, response })),
            catchError((error) => {
              this._detailLoading.set(false);
              if (hasCached) return EMPTY;

              const message = extractErrorMessage(error, 'Échec du chargement des détails');
              this.notificationService.error(message, 'Erreur de chargement');
              this._detailError.set(message);
              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ transactionId, response }) => {
        this._transactionDetailCache.update((cache) => {
          const newCache = new Map(cache);
          newCache.set(transactionId, response);
          return newCache;
        });
        this._detailLoading.set(false);
      });
  }

  /**
   * Load a transaction detail by id.
   *
   * Always revalidates against the server. Any cached response keeps rendering
   * until the fresh one lands, so the page never shows a spinner over content it
   * already has — and never shows a status that changed server-side as current.
   *
   * @param transactionId - Transaction/payment id
   */
  loadTransactionDetail(transactionId: string): void {
    if (!transactionId) {
      console.warn('loadTransactionDetail called with invalid id');
      return;
    }

    this._currentTransactionId.set(transactionId);

    // switchMap in the constructor cancels any previous in-flight request.
    //
    // untracked: callers legitimately invoke this from inside an `effect`. Subject.next()
    // runs the switchMap projection synchronously, and that projection — plus the HTTP
    // interceptors on the subscribe path — reads signals. Left tracked, those reads join
    // the caller's dependency set, so every cache write would re-run the caller, which
    // would fire another request, which would write the cache again.
    untracked(() => this.detailRequest$.next(transactionId));
  }

  /** Remove all cached transaction details. */
  clearTransactionDetailCache(): void {
    this._transactionDetailCache.set(new Map());
  }

  /**
   * Evict a single transaction's cached detail so the next
   * {@link loadTransactionDetail} has nothing stale to paint while it refetches.
   */
  invalidateTransactionDetail(transactionId: string): void {
    this._transactionDetailCache.update((cache) => {
      if (!cache.has(transactionId)) return cache;
      const next = new Map(cache);
      next.delete(transactionId);
      return next;
    });
  }

  /** Reset the current transaction id and clear related error state. */
  clearCurrentTransactionDetail(): void {
    this._currentTransactionId.set(null);
    this._detailError.set(null);
  }
}
